/**
 * Single selectable radio row for filter popover option lists.
 * Option data is a plain IRmaListFilterRadioOption; selection UI from radio-group context.
 */
import FontBody2 from "../../../../../../../../../../../../components/fonts/font-body2/font-body2";
import FontBody4 from "../../../../../../../../../../../../components/fonts/font-body4/font-body4";
import { FONT_VARIANT } from "../../../../../../../../../../../../components/fonts/font-props";
import { RadioButton } from "../../../../../../../../../../../../components/inputs/radio-button/radio-button";
import ContextualFields from "./contextual-fields/contextual-fields";
import { useRmaListFilterRadioGroup } from "./rma-list-filter-radio-group-context/rma-list-filter-radio-group-context";
import "./rma-list-filter-radio-option.scss";

export type RmaListFilterOptionId = string | number;

export interface IRmaListFilterRadioOption {
    id: RmaListFilterOptionId;
    label: string;
    description?: string;
    /**
     * Additional settings shown while this option is selected
     * (e.g. date range or days-remaining editors).
     */
    contextualFields?: React.ReactNode;
}

interface IProps {
    option: IRmaListFilterRadioOption;
}

const RmaListFilterRadioOption: React.FC<IProps> = ({ option }) => {
    const { name, value, onSelect } = useRmaListFilterRadioGroup();
    const selected = value === option.id;
    const hasDescription = Boolean(option.description);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(option.id);
        }
    };

    const containerClassNames = [
        "rma-list-filter-radio-option",
        selected ? "rma-list-filter-radio-option--selected" : "",
        hasDescription ? "rma-list-filter-radio-option--with-description" : "",
    ].filter(Boolean).join(" ");

    return (
        <div
            className={containerClassNames}
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={() => onSelect(option.id)}
            onKeyDown={handleKeyDown}
        >
            <div className="rma-list-filter-radio-option__main">
                <RadioButton
                    name={name}
                    checked={selected}
                    onChange={() => onSelect(option.id)}
                />
                <span className="rma-list-filter-radio-option__text">
                    <span className="rma-list-filter-radio-option__label">
                        <FontBody2 variant={selected ? FONT_VARIANT.BOLD : undefined}>
                            {option.label}
                        </FontBody2>
                    </span>
                    {hasDescription &&
                        <span className="rma-list-filter-radio-option__description">
                            <FontBody4>{option.description}</FontBody4>
                        </span>
                    }
                </span>
            </div>
            <ContextualFields option={option} />
        </div>
    );
};

export default RmaListFilterRadioOption;
